type TelegramSafeAreaInset = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

type TelegramWebApp = {
  ready?: () => void;
  expand?: () => void;
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
  onEvent?: (eventType: string, callback: (...args: unknown[]) => void) => void;
  offEvent?: (eventType: string, callback: (...args: unknown[]) => void) => void;
  initData?: string;
  initDataUnsafe?: {
    user?: {
      id?: number;
      first_name?: string;
      last_name?: string;
      username?: string;
    };
    start_param?: string;
  };
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  close?: () => void;
  themeParams?: Record<string, string>;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  isFullscreen?: boolean;
  platform?: string;
  version?: string;
  safeAreaInset?: TelegramSafeAreaInset;
  contentSafeAreaInset?: TelegramSafeAreaInset;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export type TelegramAuthStatus =
  | 'idle'
  | 'detecting'
  | 'authenticating'
  | 'authenticated'
  | 'unavailable'
  | 'failed';

export type TelegramAuthOutcome =
  | 'authenticated'
  | 'pending_onboarding'
  | 'unavailable'
  | 'failed';

type TelegramAuthListener = (status: TelegramAuthStatus) => void;

let authStatus: TelegramAuthStatus = 'idle';
const authListeners = new Set<TelegramAuthListener>();
let bootstrapPromise: Promise<string | null> | null = null;
/** Shared in-flight Mini App auth POST; concurrent callers reuse one transaction. */
let authInFlight: Promise<TelegramAuthOutcome> | null = null;
/** Settled outcome for this JS session; reused until force rebootstrap or reset. */
let settledAuthOutcome: TelegramAuthOutcome | null = null;
/** Counts actual `/v1/auth/telegram/miniapp` network attempts (not cache hits). */
let authNetworkAttempts = 0;
let fullscreenListenersBound = false;

/** Test/instrumentation: how many times Mini App auth actually hit the network. */
export function getTelegramAuthNetworkAttemptCount(): number {
  return authNetworkAttempts;
}

/** Test helper: reset app-scoped auth bootstrap (does not clear WebApp script state). */
export function resetTelegramAuthBootstrapForTests(): void {
  authStatus = 'idle';
  bootstrapPromise = null;
  authInFlight = null;
  settledAuthOutcome = null;
  authNetworkAttempts = 0;
  for (const listener of authListeners) listener(authStatus);
}

export function getTelegramWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

export function getTelegramInitData(): string | null {
  const tg = getTelegramWebApp();
  if (tg?.initData && tg.initData.trim()) return tg.initData;
  const params = new URLSearchParams(window.location.search);
  const data = params.get('tgWebAppData');
  if (data && data.trim()) return data;
  return null;
}

/** Payload from YooKassa return deep link: pay_<orderId> */
export function getTelegramStartParam(): string | null {
  const fromUnsafe = getTelegramWebApp()?.initDataUnsafe?.start_param?.trim();
  if (fromUnsafe) return fromUnsafe;
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('tgWebAppStartParam')?.trim();
  return fromQuery || null;
}

export function isTelegramMiniApp(): boolean {
  return Boolean(getTelegramInitData());
}

export function looksLikeTelegramMiniAppShell(): boolean {
  return looksLikeTelegramShell();
}

export function getTelegramAuthStatus(): TelegramAuthStatus {
  return authStatus;
}

export function subscribeTelegramAuthStatus(listener: TelegramAuthListener): () => void {
  authListeners.add(listener);
  listener(authStatus);
  return () => {
    authListeners.delete(listener);
  };
}

function setTelegramAuthStatus(next: TelegramAuthStatus): void {
  if (authStatus === next) return;
  authStatus = next;
  for (const listener of authListeners) listener(next);
}

export function openExternalUrl(url: string): void {
  const tg = getTelegramWebApp();
  if (tg?.openLink) {
    tg.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function looksLikeTelegramShell(): boolean {
  if (getTelegramWebApp()?.initData) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.has('tgWebAppData') || params.has('tgWebAppVersion')) return true;
  return /Telegram/i.test(navigator.userAgent);
}

function loadTelegramScript(): Promise<void> {
  if (window.Telegram?.WebApp) return Promise.resolve();
  return new Promise(resolve => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-telegram-web-app], script[src*="telegram-web-app.js"]');
    if (existing) {
      if (window.Telegram?.WebApp) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => resolve(), { once: true });
      // Script tag may already be loaded without firing again.
      window.setTimeout(() => resolve(), 0);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js';
    script.async = true;
    script.dataset.telegramWebApp = '1';
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

function applyInsetVars(prefix: string, inset?: TelegramSafeAreaInset): void {
  if (!inset) return;
  const root = document.documentElement.style;
  if (typeof inset.top === 'number') root.setProperty(`${prefix}-top`, `${inset.top}px`);
  if (typeof inset.bottom === 'number') root.setProperty(`${prefix}-bottom`, `${inset.bottom}px`);
  if (typeof inset.left === 'number') root.setProperty(`${prefix}-left`, `${inset.left}px`);
  if (typeof inset.right === 'number') root.setProperty(`${prefix}-right`, `${inset.right}px`);
}

function syncTelegramViewportCss(tg: TelegramWebApp): void {
  const root = document.documentElement.style;
  const height = tg.viewportStableHeight ?? tg.viewportHeight;
  if (typeof height === 'number' && height > 0) {
    root.setProperty('--tg-viewport-stable-height', `${height}px`);
    root.setProperty('--tg-viewport-height', `${height}px`);
  }
  applyInsetVars('--tg-safe-area', tg.safeAreaInset);
  applyInsetVars('--tg-content-safe-area', tg.contentSafeAreaInset);
  document.documentElement.classList.toggle('tg-fullscreen', Boolean(tg.isFullscreen));
}

function supportsFullscreen(tg: TelegramWebApp): boolean {
  if (typeof tg.requestFullscreen !== 'function') return false;
  if (typeof tg.isVersionAtLeast === 'function') {
    try {
      return tg.isVersionAtLeast('8.0');
    } catch {
      return true;
    }
  }
  return true;
}

function requestTelegramFullscreen(tg: TelegramWebApp): void {
  if (!supportsFullscreen(tg)) return;
  if (tg.isFullscreen) {
    document.documentElement.classList.add('tg-fullscreen');
    return;
  }
  try {
    tg.requestFullscreen?.();
  } catch {
    // Older / unsupported clients reject; expand() already applied.
  }
}

function bindFullscreenListeners(tg: TelegramWebApp): void {
  if (fullscreenListenersBound || typeof tg.onEvent !== 'function') return;
  fullscreenListenersBound = true;

  const sync = () => {
    syncTelegramViewportCss(tg);
    if (!tg.isFullscreen) requestTelegramFullscreen(tg);
  };

  tg.onEvent('fullscreenChanged', sync);
  tg.onEvent('fullscreenFailed', () => {
    // Keep expanded sheet height even if true fullscreen is unavailable.
    try {
      tg.expand?.();
    } catch {
      // ignore
    }
    syncTelegramViewportCss(tg);
  });
  tg.onEvent('viewportChanged', sync);
  tg.onEvent('safeAreaChanged', sync);
  tg.onEvent('contentSafeAreaChanged', sync);
  tg.onEvent('activated', () => {
    try {
      tg.expand?.();
    } catch {
      // ignore
    }
    requestTelegramFullscreen(tg);
    syncTelegramViewportCss(tg);
  });
}

function applyTelegramChrome(tg: TelegramWebApp): void {
  try {
    tg.ready?.();
    tg.expand?.();
    tg.setHeaderColor?.('#120d1b');
    tg.setBackgroundColor?.('#120d1b');
    tg.setBottomBarColor?.('#120d1b');
    tg.disableVerticalSwipes?.();
    requestTelegramFullscreen(tg);
    // Retry: some clients accept fullscreen only after the first paint / activation.
    window.setTimeout(() => requestTelegramFullscreen(tg), 50);
    window.setTimeout(() => requestTelegramFullscreen(tg), 250);
    window.setTimeout(() => requestTelegramFullscreen(tg), 800);
  } catch {
    // Older Telegram clients may lack some methods.
  }
  document.documentElement.classList.add('tg-mini-app');
  bindFullscreenListeners(tg);
  syncTelegramViewportCss(tg);
}

/** Soften Telegram chrome and claim vertical space inside Mini App. Non-blocking for regular web. */
export function bootstrapTelegramMiniApp(): void {
  if (!looksLikeTelegramShell()) return;
  void ensureTelegramWebAppReady();
}

export async function ensureTelegramWebAppReady(timeoutMs = 2500): Promise<string | null> {
  if (bootstrapPromise) return bootstrapPromise;
  if (!looksLikeTelegramShell()) {
    setTelegramAuthStatus('unavailable');
    return null;
  }

  bootstrapPromise = (async () => {
    setTelegramAuthStatus('detecting');
    await loadTelegramScript();
    const tg = getTelegramWebApp();
    if (tg) applyTelegramChrome(tg);

    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const initData = getTelegramInitData();
      if (initData) return initData;
      await new Promise(resolve => window.setTimeout(resolve, 50));
    }
    return getTelegramInitData();
  })();

  return bootstrapPromise;
}

export async function authenticateTelegramMiniApp(options: {
  post: <T>(path: string, body: unknown, idempotencyKey?: string) => Promise<T>;
  createSession: () => Promise<void>;
  /** Re-run Telegram auth even if this session already settled (session expiry recovery). */
  force?: boolean;
}): Promise<TelegramAuthOutcome> {
  if (!options.force) {
    if (authInFlight) return authInFlight;
    if (
      settledAuthOutcome
      && (authStatus === 'authenticated' || authStatus === 'unavailable')
    ) {
      return settledAuthOutcome;
    }
  } else if (authInFlight) {
    // Wait for the current attempt, then force a fresh one below.
    try {
      await authInFlight;
    } catch {
      // ignore; force path continues
    }
  }

  const run = async (): Promise<TelegramAuthOutcome> => {
    const initData = await ensureTelegramWebAppReady();
    if (!initData) {
      const outcome: TelegramAuthOutcome = looksLikeTelegramShell() ? 'failed' : 'unavailable';
      setTelegramAuthStatus(outcome === 'failed' ? 'failed' : 'unavailable');
      settledAuthOutcome = outcome;
      return outcome;
    }

    setTelegramAuthStatus('authenticating');

    const attempt = async () => {
      authNetworkAttempts += 1;
      return options.post<{
        outcome: 'linked' | 'restored' | 'pending_onboarding' | 'conflict';
      }>('/v1/auth/telegram/miniapp', { initData });
    };

    try {
      let result;
      try {
        result = await attempt();
      } catch (error) {
        const code = error && typeof error === 'object' && 'code' in error
          ? String((error as { code?: string }).code ?? '')
          : '';
        if (code === 'NEED_SESSION') {
          await options.createSession();
          result = await attempt();
        } else {
          throw error;
        }
      }

      if (result.outcome === 'conflict') {
        setTelegramAuthStatus('failed');
        settledAuthOutcome = 'failed';
        return 'failed';
      }
      if (result.outcome === 'pending_onboarding') {
        setTelegramAuthStatus('authenticated');
        settledAuthOutcome = 'pending_onboarding';
        return 'pending_onboarding';
      }
      setTelegramAuthStatus('authenticated');
      settledAuthOutcome = 'authenticated';
      return 'authenticated';
    } catch {
      setTelegramAuthStatus('failed');
      settledAuthOutcome = 'failed';
      return 'failed';
    }
  };

  authInFlight = run();
  try {
    return await authInFlight;
  } finally {
    authInFlight = null;
  }
}

/** True while Mini App identity bootstrap is still in progress. Paid CTAs must wait. */
export function isTelegramAuthPending(status: TelegramAuthStatus = authStatus): boolean {
  return status === 'detecting' || status === 'authenticating' || status === 'idle';
}
